import { useState, useEffect, useMemo, useCallback } from "react";
import { apiService } from "../../../../services/api";
import { parseApiError } from "../../../../services/api/errorPresentation";
import { calculateYearlyCosts } from "./calculateYearlyCosts";
import {
  Capex,
  CapexItem,
  Opex,
  OpexItem,
  CapexItemCreate,
  CapexItemUpdate,
  OpexItemCreate,
  OpexItemUpdate,
  LeaseTier,
  LeaseTierCreate,
  LeaseTierUpdate,
  YearlyCostsResponse,
  EnergyRevenuesResponse,
} from "../../../../types";
import {
  hasFieldChanged,
  hasAnyFieldChanged,
} from "../../../../utils/formUtils";

interface UseCostsDataProps {
  projectId: string;
  laufzeitJahre: number;
}

interface UseCostsDataReturn {
  capex: Capex | null;
  opex: Opex | null;
  yearlyCosts: YearlyCostsResponse | null;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
  // CAPEX Item-Methoden
  createCapexItem: (data: CapexItemCreate) => Promise<void>;
  updateCapexItem: (itemId: string, data: CapexItemUpdate) => Promise<void>;
  deleteCapexItem: (itemId: string) => Promise<void>;
  // OPEX Item-Methoden
  createOpexItem: (data: OpexItemCreate) => Promise<void>;
  updateOpexItem: (itemId: string, data: OpexItemUpdate) => Promise<void>;
  deleteOpexItem: (itemId: string) => Promise<void>;
  // Manuelle Jahreswerte
  updateYearlyValue: (itemId: string, jahr: number, wert: number) => void;
  // LeaseTier-Methoden
  createLeaseTier: (data: LeaseTierCreate) => Promise<void>;
  updateLeaseTier: (tierId: string, data: LeaseTierUpdate) => Promise<void>;
  deleteLeaseTier: (tierId: string) => Promise<void>;
  // Section-Label-Methoden
  setGenehmigungOverride: (wert: number | null) => Promise<void>;
  updateCapexSectionLabel: (sectionKey: string, label: string) => Promise<void>;
  updateOpexSectionLabel: (sectionKey: string, label: string) => Promise<void>;
}

/**
 * Hook für das Laden und Aktualisieren von CAPEX/OPEX-Daten.
 *
 * Performance-optimiert nach YieldPage-Pattern:
 * - 2 parallele initiale API-Calls (Costs + EnergyRevenues)
 * - YearlyCosts client-seitig via useMemo berechnet (kein getYearlyCosts-Call)
 * - Granulare lokale Updates nach Mutationen (kein Entity-Reload)
 * - LeaseTier rein lokal (Option C)
 * - Fire-and-forget: Backend wird über Neuberechnung informiert
 *
 * Duale Berechnung: calculateYearlyCosts entspricht calculate_yearly_costs
 * im Backend (domain/costs/service.py). Shared Fixtures stellen Konsistenz sicher.
 */
export const useCostsData = ({
  projectId,
  laufzeitJahre,
}: UseCostsDataProps): UseCostsDataReturn => {
  // Rohdaten vom Backend (Source of Truth)
  const [capex, setCapex] = useState<Capex | null>(null);
  const [opex, setOpex] = useState<Opex | null>(null);
  const [energyRevenues, setEnergyRevenues] =
    useState<EnergyRevenuesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Client-seitige Berechnung der YearlyCosts (ersetzt getYearlyCosts API)

  const yearlyCosts = useMemo<YearlyCostsResponse | null>(
    () => calculateYearlyCosts(capex, opex, energyRevenues, laufzeitJahre),
    [capex, opex, energyRevenues, laufzeitJahre],
  );

  // Initialer Load (2 parallele Calls)

  const loadCosts = useCallback(async () => {
    if (!projectId) return;
    try {
      setIsLoading(true);
      setError(null);

      const [costs, revenues] = await Promise.all([
        apiService.getProjectCosts(projectId),
        apiService.getEnergyRevenues(projectId),
      ]);

      setCapex(costs.capex || null);
      setOpex(costs.opex || null);
      setEnergyRevenues(revenues);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  /** Ersetzt ein CAPEX-Item im lokalen State mit der Backend-Response */
  const updateCapexItemLocally = useCallback((updatedItem: CapexItem) => {
    setCapex((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((i) =>
          i.id === updatedItem.id ? updatedItem : i,
        ),
      };
    });
  }, []);

  /** Fügt ein neues CAPEX-Item zum lokalen State hinzu */
  const addCapexItemLocally = useCallback((newItem: CapexItem) => {
    setCapex((prev) => {
      if (!prev) return prev;
      return { ...prev, items: [...prev.items, newItem] };
    });
  }, []);

  /** Entfernt ein CAPEX-Item aus dem lokalen State */
  const removeCapexItemLocally = useCallback((itemId: string) => {
    setCapex((prev) => {
      if (!prev) return prev;
      return { ...prev, items: prev.items.filter((i) => i.id !== itemId) };
    });
  }, []);

  /** Ersetzt ein OPEX-Item im lokalen State mit der Backend-Response */
  const updateOpexItemLocally = useCallback((updatedItem: OpexItem) => {
    setOpex((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((i) =>
          i.id === updatedItem.id ? updatedItem : i,
        ),
      };
    });
  }, []);

  /** Fügt ein neues OPEX-Item zum lokalen State hinzu */
  const addOpexItemLocally = useCallback((newItem: OpexItem) => {
    setOpex((prev) => {
      if (!prev) return prev;
      return { ...prev, items: [...prev.items, newItem] };
    });
  }, []);

  /** Entfernt ein OPEX-Item aus dem lokalen State */
  const removeOpexItemLocally = useCallback((itemId: string) => {
    setOpex((prev) => {
      if (!prev) return prev;
      return { ...prev, items: prev.items.filter((i) => i.id !== itemId) };
    });
  }, []);

  /**
   * Aktualisiert ein LeaseTier im lokalen Pacht-OpexItem (Option C).
   * Rechnet den basiswert für Modus "prozent" client-seitig nach.
   */
  const updateLeaseTierInOpex = useCallback(
    (updatedTier: LeaseTier, mode: "add" | "update" | "remove") => {
      setOpex((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== updatedTier.cost_item_id) return item;

            let newTiers: LeaseTier[];
            if (mode === "add") {
              newTiers = [...(item.lease_tiers ?? []), updatedTier].sort(
                (a, b) => a.first_year - b.first_year,
              );
            } else if (mode === "update") {
              newTiers = (item.lease_tiers ?? [])
                .map((t) => (t.id === updatedTier.id ? updatedTier : t))
                .sort((a, b) => a.first_year - b.first_year);
            } else {
              newTiers = (item.lease_tiers ?? []).filter(
                (t) => t.id !== updatedTier.id,
              );
            }

            // basiswert client-seitig nachrechnen (Modus "prozent")
            let basiswert = item.basiswert;
            if (item.modus === "prozent") {
              const erloes1 =
                energyRevenues?.eintraege?.find((e) => e.jahr === 1)
                  ?.erloes_eur ?? 0;
              const pct = newTiers[0]?.percentage ?? 0;
              basiswert = erloes1 > 0 && pct > 0 ? (erloes1 * pct) / 100 : 0;
            }

            return { ...item, lease_tiers: newTiers, basiswert };
          }),
        };
      });
    },
    [energyRevenues],
  );

  // CAPEX Item-Methoden

  const createCapexItem = useCallback(
    async (data: CapexItemCreate) => {
      try {
        setIsSaving(true);
        const newItem = await apiService.createCapexItem(projectId, data);
        addCapexItemLocally(newItem);
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, addCapexItemLocally],
  );

  const updateCapexItem = useCallback(
    async (itemId: string, data: CapexItemUpdate) => {
      const currentItem = capex?.items.find((i) => i.id === itemId);
      if (currentItem && !hasAnyFieldChanged(currentItem, data)) return;

      // Optimistisches Update
      setCapex((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== itemId) return item;
            return {
              ...item,
              ...(data.bezeichnung !== undefined && {
                bezeichnung: data.bezeichnung,
              }),
              ...(data.wert !== undefined && { wert: data.wert }),
              ...(data.abschreibungsdauer !== undefined && {
                abschreibungsdauer: data.abschreibungsdauer,
              }),
              ...(data.is_per_unit !== undefined && {
                is_per_unit: data.is_per_unit,
              }),
            };
          }),
        };
      });

      try {
        setIsSaving(true);
        const updatedItem = await apiService.updateCapexItem(
          projectId,
          itemId,
          data,
        );
        updateCapexItemLocally(updatedItem);
      } catch (err) {
        setError(parseApiError(err).message);
        const freshCapex = await apiService.getCapex(projectId);
        setCapex(freshCapex);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, capex, updateCapexItemLocally],
  );

  const deleteCapexItem = useCallback(
    async (itemId: string) => {
      // Optimistisch entfernen
      removeCapexItemLocally(itemId);

      try {
        setIsSaving(true);
        await apiService.deleteCapexItem(projectId, itemId);
      } catch (err) {
        setError(parseApiError(err).message);
        // Bei Fehler: CAPEX komplett neu laden
        const freshCapex = await apiService.getCapex(projectId);
        setCapex(freshCapex);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, removeCapexItemLocally],
  );

  // OPEX Item-Methoden

  const createOpexItem = useCallback(
    async (data: OpexItemCreate) => {
      try {
        setIsSaving(true);
        const newItem = await apiService.createOpexItem(projectId, data);
        addOpexItemLocally(newItem);
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, addOpexItemLocally],
  );

  const updateOpexItem = useCallback(
    async (itemId: string, data: OpexItemUpdate) => {
      const currentItem = opex?.items.find((i) => i.id === itemId);
      if (currentItem && !hasAnyFieldChanged(currentItem, data)) return;

      // Optimistisches Update
      setOpex((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== itemId) return item;
            return {
              ...item,
              ...(data.bezeichnung !== undefined && {
                bezeichnung: data.bezeichnung,
              }),
              ...(data.basiswert !== undefined && {
                basiswert: data.basiswert,
              }),
              ...(data.modus !== undefined && { modus: data.modus }),
              ...(data.steigerung !== undefined && {
                steigerung: data.steigerung,
              }),
              ...(data.prozent_satz !== undefined && {
                prozent_satz: data.prozent_satz,
              }),
            };
          }),
        };
      });

      try {
        setIsSaving(true);
        const updatedItem = await apiService.updateOpexItem(
          projectId,
          itemId,
          data,
        );
        updateOpexItemLocally(updatedItem);
      } catch (err) {
        setError(parseApiError(err).message);
        const freshOpex = await apiService.getOpex(projectId);
        setOpex(freshOpex);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, opex, updateOpexItemLocally],
  );

  const deleteOpexItem = useCallback(
    async (itemId: string) => {
      removeOpexItemLocally(itemId);

      try {
        setIsSaving(true);
        await apiService.deleteOpexItem(projectId, itemId);
      } catch (err) {
        setError(parseApiError(err).message);
        const freshOpex = await apiService.getOpex(projectId);
        setOpex(freshOpex);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, removeOpexItemLocally],
  );

  // Manuelle Jahreswerte (Modus "manuell")

  const updateYearlyValue = useCallback(
    async (itemId: string, jahr: number, wert: number) => {
      const currentItem = opex?.items.find((i) => i.id === itemId);
      const currentManualValue = currentItem?.manual_values.find(
        (mv) => mv.jahr === jahr,
      );
      if (
        currentManualValue &&
        !hasFieldChanged(currentManualValue, "wert", wert)
      )
        return;

      // Optimistisches Update: manual_values im OpexItem aktualisieren
      setOpex((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== itemId) return item;
            const existingIdx = item.manual_values.findIndex(
              (mv) => mv.jahr === jahr,
            );
            const updatedValues = [...item.manual_values];
            if (existingIdx >= 0) {
              updatedValues[existingIdx] = {
                ...updatedValues[existingIdx],
                wert,
              };
            } else {
              updatedValues.push({
                id: `temp-${jahr}`,
                cost_item_id: itemId,
                jahr,
                wert,
                created_at: "",
              });
            }
            return { ...item, manual_values: updatedValues };
          }),
        };
      });

      try {
        setIsSaving(true);
        await apiService.updateYearlyValue(projectId, itemId, jahr, { wert });
      } catch (err) {
        setError(parseApiError(err).message);
        // Bei Fehler: OPEX komplett neu laden
        const freshOpex = await apiService.getOpex(projectId);
        setOpex(freshOpex);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, opex],
  );

  // LeaseTier-Methoden (Option C: rein lokal)

  const createLeaseTier = useCallback(
    async (data: LeaseTierCreate) => {
      try {
        setIsSaving(true);
        const newTier = await apiService.createLeaseTier(projectId, data);
        updateLeaseTierInOpex(newTier, "add");
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, updateLeaseTierInOpex],
  );

  const updateLeaseTier = useCallback(
    async (tierId: string, data: LeaseTierUpdate) => {
      const currentTier = opex?.items
        .flatMap((item) => item.lease_tiers ?? [])
        .find((t) => t.id === tierId);
      if (currentTier && !hasAnyFieldChanged(currentTier, data)) return;

      try {
        setIsSaving(true);
        const updatedTier = await apiService.updateLeaseTier(
          projectId,
          tierId,
          data,
        );
        updateLeaseTierInOpex(updatedTier, "update");
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, opex, updateLeaseTierInOpex],
  );

  const deleteLeaseTier = useCallback(
    async (tierId: string) => {
      // Für das lokale Entfernen brauchen wir die cost_item_id – diese finden wir im State
      const tierToDelete = opex?.items
        .flatMap((item) => item.lease_tiers ?? [])
        .find((t) => t.id === tierId);

      if (!tierToDelete) return;

      try {
        setIsSaving(true);
        await apiService.deleteLeaseTier(projectId, tierId);
        updateLeaseTierInOpex(tierToDelete, "remove");
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, opex, updateLeaseTierInOpex],
  );

  const updateCapexSectionLabel = useCallback(
    async (sectionKey: string, label: string) => {
      try {
        setIsSaving(true);
        const updatedCapex = await apiService.updateCapexSectionLabel(
          projectId,
          sectionKey,
          label,
        );
        setCapex(updatedCapex);
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId],
  );

  /**
   * Setzt die manuelle Summe des Genehmigungs-Abschnitts (`null` = zurück auf die aus dem
   * Genehmigungsverfahren abgeleiteten Planwerte).
   */
  const setGenehmigungOverride = useCallback(
    async (wert: number | null) => {
      try {
        setIsSaving(true);
        const updatedCapex = await apiService.setGenehmigungOverride(
          projectId,
          wert,
        );
        setCapex(updatedCapex);
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId],
  );

  const updateOpexSectionLabel = useCallback(
    async (sectionKey: string, label: string) => {
      try {
        setIsSaving(true);
        const updatedOpex = await apiService.updateOpexSectionLabel(
          projectId,
          sectionKey,
          label,
        );
        setOpex(updatedOpex);
      } catch (err) {
        setError(parseApiError(err).message);
      } finally {
        setIsSaving(false);
      }
    },
    [projectId],
  );

  return {
    capex,
    opex,
    yearlyCosts,
    isLoading,
    error,
    isSaving,
    createCapexItem,
    updateCapexItem,
    deleteCapexItem,
    createOpexItem,
    updateOpexItem,
    deleteOpexItem,
    updateYearlyValue,
    createLeaseTier,
    updateLeaseTier,
    deleteLeaseTier,
    setGenehmigungOverride,
    updateCapexSectionLabel,
    updateOpexSectionLabel,
  };
};
